import { ActiveProjects, ArchivedProjects, Projects, Project } from '@/models/Project';
import { GetCategories } from '@/models/Category';
import { KeywordLists, Keywords } from '@/models/Keyword';
import { Outputs } from '@/models/Output';
import { CustomDatas, CustomDataItems } from '@/models/CustomData';
import { Overrides } from '@/models/Override';

import ls from '@/util/ls';
import { make } from 'vuex-pathify';

const state = {
	active: new ActiveProjects(),
	archived: new ArchivedProjects(),
	currentProject: null,
};

const getters = {

	/**
	 * Returns true if there is only one project in active projects,
	 * false otherwise
	 *
	 * @param {Object} state This store
	 * @returns {Boolean}
	 */
	isSingleProject: (state) => state.active.length === 1,

	/**
	 * Returns all projects, active and archived
	 *
	 * @param {Object} state This store
	 * @returns {Projects} Combined active and archived project collection
	 */
	all: (state) => {
		const models = state.active.models.concat(state.archived.models);
		return new Projects(models);
	},

	/**
	 * Finds if store has current project set
	 *
	 * @returns {boolean}
	 */
	hasCurrentProject: (state) => {
		return (state.currentProject !== null && state.currentProject instanceof Project);
	},

	/**
	 * Returns recent projects, base on localStorage record,
	 * if no record matched, returns empty project collection
	 *
	 * @param {Object} state This store
	 * @returns {Projects} Recent project collection
	 */
	recent: (state) => () => {
		const projects = new Projects();
		const userId = ls.get('id', null);

		if (userId === null) {
			return projects;
		}

		const recentProjects = ls.get('recentProjects', { uid: null, items: [] });

		if (recentProjects.uid !== userId) {
			ls.set('recentProjects', { uid: userId, items: [] });
			return projects;
		}

		if (recentProjects.items.length > 0) {
			recentProjects.items.forEach((id) => {
				const project = state.active.find({ id });
				if (!project) {
					return;
				}
				projects.add(project);
			});
		}

		return projects;
	}

};

const mutations = {
	...make.mutations(state),
};

const actions = {

	/**
	 * Loads all projects (active and archived), to which this user have access,
	 * then filters in two groups for active and archived and adds to the store
	 *
	 * @param {Object} param0 Destructed context object
	 * @returns {Promise} Result of fetching all projects from backend
	 */
	loadAll({ state, commit }) {
		return new Promise((resolve, reject) => {

			// Clear the state
			state.active.clear();
			state.archived.clear();

			const projects = new Projects();
			projects.fetch().then((resp) => {

				// Set if the app uses productionDB
				const isProductionDB = resp.response.headers['x-debug-db'] === 'prod';
				commit('SET_PRODUCTION_DB', isProductionDB, { root: true });

				projects.each(project => {
					project.isArchived ? state.archived.add(project) : state.active.add(project);
				});

				resolve(projects);
			}).catch((e) => {
				dump('Error fetching all projects', e);
				reject(e);
			});
		});
	},

	/**
	 * Loads active projects, to which this user have access
	 *
	 * @param {Object} param0 Destructed context object
	 * @returns {Promise}
	 */
	loadActive({ state }) {
		return new Promise((resolve, reject) => {
			state.active.fetch().then((resp) => {
				resolve(state.active);
			}).catch((e) => {
				dump('Error fetching active projects', e);
				reject(e);
			});
		});
	},

	/**
	 * Loads archived projects, to which this user have access
	 *
	 * @param {Object} param0 Destructed context object
	 * @returns {Promise} Result of fetching archived projects from backend
	 */
	loadArchived({ state }) {
		return new Promise((resolve, reject) => {
			state.archived.fetch().then((resp) => {
				resolve(state.archived);
			}).catch((e) => {
				dump('Error fetching archived projects', e);
				reject(e);
			});
		});
	},

	/**
	 * Loads project by project ID and related data for this project:
	 * categories, outputs, users, keywords, and reports.
	 *
	 * @param {Object} param0 Destructed context object
	 * @param {Project} projectId Project, for which load related data
	 * @returns {Promise} Result of fetching archived projects from backend
	 */
	loadCurrentProjectAndRelatedData({ dispatch }, projectId) {

		dispatch('addToRecentProjects', projectId);

		let promises = [
			dispatch('loadCurrentProject', projectId),
			dispatch('loadCategories'),
			dispatch('loadOutputs'),
			dispatch('loadUsers'),
			dispatch('loadCustomData'),
			dispatch('loadKeywordsList'),
			dispatch('loadOverrides'),
			dispatch('reports/loadAll', projectId, { root: true }),
		];

		return Promise.all(promises);
	},

	/**
	 * Sets current project based on ID, search in assigned projects,
	 * if not there, fetches it
	 *
	 * @param {Object} param0 Destructed context object
	 * @param {number} projectId Project ID of project, which will be current project
	 * @returns {Promise} Result of fetching archived projects from backend
	 */
	loadCurrentProject({ getters, commit }, projectId) {
		return new Promise((resolve, reject) => {
			let project = null;
			// Check if project in store already
			try {
				project = getters.all.find({ id: projectId });

				if (project) {
					commit('SET_CURRENT_PROJECT', project);
					dump(`Project ${project.id} permissions`, project.permissions);
					resolve(project);
					return;
				}
			}
			catch (e) {
				dump('Error finding current project in all projects', e);
				reject(e);
				return;
			}

			project = new Project({ id: projectId });
			commit('SET_CURRENT_PROJECT', project);
			dump(`Project ${project.id} permissions`, project.permissions);

			project.fetch().then((resp) => {
				resolve(project);
			}).catch((e) => {
				dump('Error fetching current project', e);
				reject(e);
			});

		});
	},

	/**
	 * Sets active and archived categories to current project
	 *
	 * @param {Object} param0 Destructed context object
	 * @returns {Promise} Result of fetching categories, filtered by active and archived
	 */
	loadCategories({ state, getters, commit }) {
		return new Promise((resolve, reject) => {
			// Check if store has current project
			if (!getters.hasCurrentProject) {
				reject(new Error('Current project is not set'));
				return;
			}

			const categories = new GetCategories();
			categories.set('projectId', state.currentProject.id);
			categories.fetch().then(() => {
				// Vue mc attribute refs get removed if we try to create trees so we have to create
				// a new array for the categories
				let categoryList = [];
				categories.each((cat) => {
					categoryList.push({
						id: cat.id,
						name: cat.name,
						level: cat.level,
						isArchived: cat.isArchived,
					});
				});

				/** @todo Find better way to filter them */
				const activeCategories = categoryList.filter(o => !o.isArchived);
				const archivedCategories = categoryList.filter(o => o.isArchived);

				state.currentProject.activeCategories = activeCategories;
				state.currentProject.archivedCategories = archivedCategories;

				/**
				 * Set to old store as well for now
				 * @todo To be deleted
				 */
				commit('SET_CATEGORIES', activeCategories, { root: true });
				commit('SET_CATEGORIES_ARCHIVED', archivedCategories, { root: true });

				resolve({ activeCategories, archivedCategories });
			}).catch((e) => {
				dump('Error fetching categories', e);
				reject(e);
			});
		});
	},

	/**
	 * Load outputs for current project
	 *
	 * @param {Object} param0 Destructed context object
	 * @returns {Promise} Result of fetching outputs for current project
	 */
	loadOutputs({ state, getters }) {
		return new Promise((resolve, reject) => {
			// Check if store has current project
			if (!getters.hasCurrentProject) {
				reject(new Error('Current project is not set'));
				return;
			}

			const outputs = new Outputs();
			outputs.set('projectId', state.currentProject.id);
			outputs.fetch().then((resp) => {
				state.currentProject.outputs = outputs.models;
				resolve(outputs.models);
			}).catch((e) => {
				dump('Error fetching outputs for current project', e);
				reject(e);
			});
		});
	},

	/**
	 * Load overrides for current project
	 *
	 * @param {Object} param0 Destructed context object
	 * @returns {Promise} Result of fetching overrides for current project
	 */
	loadOverrides({ state, getters }) {
		return new Promise((resolve, reject) => {
			// Check if store has current project
			if (!getters.hasCurrentProject) {
				reject(new Error('Current project is not set'));
				return;
			}

			const collection = new Overrides();
			collection.set('projectId', state.currentProject.id);
			collection.fetch().then((resp) => {
				state.currentProject.overrides = collection.models;
				resolve(collection.models);
			}).catch((e) => {
				dump('Error fetching overrides for current project', e);
				reject(e);
			});
		});
	},
	
	/**
	 * @todo NOT FINISHED, CHECK LOGIC!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
	 *
	 * @param {Object} param0
	 * @returns {Promise} Result of fetching keywords list for current project
	 */
	loadKeywordsList({ state, getters, commit }) {
		return new Promise((resolve, reject) => {
			// Check if store has current project
			if (!getters.hasCurrentProject) {
				reject(new Error('Current project is not set'));
				return;
			}

			const keywordLists = new KeywordLists();
			keywordLists.set('id', state.currentProject.id);
			keywordLists.fetch().then(() => {
				// Guard for migration of keywordsList fetch containing keywords
				// remove else when completed
				if (keywordLists.length > 0 && keywordLists.first().keywords) {
					/**
					 * Set to old store as well for now
					 * @todo To be deleted
					 */
					commit('SET_KEYWORD_LISTS', keywordLists.models, { root: true });
					state.currentProject.keywordLists = keywordLists.models;
				}
				else {
					/** @todo Fix this, too many requests */
					keywordLists.models.forEach((list) => {
						const keywords = new Keywords();
						keywords.set('id', list.id);
						keywords.fetch().then(() => {
							list.keywords = keywords.models;
						}).catch(() => {
							list.keywords = [];
							dump('Error fetching keywords', e);
							reject(e);
						});
					});

					commit('SET_KEYWORD_LISTS', keywordLists.models, { root: true });
					state.currentProject.keywordLists = keywordLists.models;
				}
				resolve(keywordLists.models);
			}).catch((e) => {
				dump('Error fetching keyword list', e);
				reject(e);
			});
		});
	},

	/**
	 * Loads users and attaches them to current project
	 * First, fetches users IDs, who have access to current project,
	 * then finds them in all users current user can see
	 * @todo Load users by project ID, not by store's current project
	 *
	 * @param {Object} param0 Destructed context object
	 * @returns {Promise} Result of fetching users for current project
	 */
	loadUsers({ state, getters, rootState }) {
		return new Promise((resolve, reject) => {
			// Check if store has current project
			if (!getters.hasCurrentProject) {
				reject(new Error('Current project is not set'));
				return;
			}

			// If current user doesn't have permission, return empty []
			if (!state.currentProject.userCan('manage_users')) {
				resolve([]);
				return;
			}

			state.currentProject.fetchUsers().then((resp) => {
				let usersMap = new Map();
				rootState.users.users.each(user => {
					usersMap.set(user.id, user);
				});

				let users = [];
				resp.response.data.forEach(user => {
					if (!usersMap.has(user.userId)) {
						return;
					}

					const userFromStore = usersMap.get(user.userId).clone();
					userFromStore.access = user.accessLevel;
					users.push(userFromStore);
				});

				state.currentProject.users = users;
				resolve(users);
			})
			.catch((e) =>{
				dump('Error fetching users for project', e);
				reject(e);
			});
		});
	},
	
	loadCustomData({ state, getters }) {
		return new Promise((resolve, reject) => {
			// Check if store has current project
			if (!getters.hasCurrentProject) {
				reject(new Error('Current project is not set'));
				return;
			}

			// If current user doesn't have permission, return empty []
			if (!state.currentProject.userCan('view_custom_data')) {
				resolve([]);
				return;
			}

			const customDatas = new CustomDatas();
			customDatas.set('projectId', state.currentProject.id);
			customDatas.fetch().then((resp) => {
				customDatas.models.forEach(fieldData => {
					const items = fieldData.items;
					items.sort((a, b) => a.order - b.order);
					fieldData.items = new CustomDataItems(items);
				});
				state.currentProject.customDatas = customDatas;
				resolve(customDatas);
			}).catch((e) => {
				dump('Error fetching custom data for current project', e);
				reject(e);
			});
		});
	},

	/**
	 * Archives project, moves project from active collection to archived and removes it from localStorage
	 * @todo Add check if successful, maybe return promise with try/catch
	 *
	 * @param {Object} param0 Destructed context object
	 * @param {Project} project Selected project
	 */
	archive({ state, dispatch }, project) {
		state.active.remove(project);
		state.archived.add(project);
		dispatch('removeFromRecentProjects', project.id);
	},

	/**
	 * Restores project, moves project from archived collection to active
	 * @todo Add check if successful, maybe return promise with try/catch
	 *
	 * @param {Object} param0 Destructed context object
	 * @param {Project} project Selected project
	 */
	restore({ state }, project) {
		state.archived.remove(project);
		state.active.add(project);
	},

	/**
	 * Adds project ID to recent projects, stored in localStorage
	 * @todo Probably need to move somewhere else, doesn't really relate to this store
	 *
	 * @param {Object} context Context object
	 * @param {number} projectId Selected project ID
	 */
	addToRecentProjects(context, projectId) {
		if (!Number.isInteger(projectId)) {
			return;
		}
		
		let recentProjects = ls.get('recentProjects', { uid: null, items: [] });
		recentProjects.uid = ls.get('id', null);

		// Find this project in recent projects,
		// If there, remove id
		const projectIndexInRecentProjects = recentProjects.items.indexOf(projectId);
		if (projectIndexInRecentProjects >= 0) {
			recentProjects.items.splice(projectIndexInRecentProjects, 1);
		}

		// Add this project to the beginning of recent projects
		recentProjects.items.unshift(projectId);
		// Take first 4 recent project IDs
		recentProjects.items = recentProjects.items.slice(0, 4);

		ls.set('recentProjects', recentProjects);
	},

	/**
	 * Remove project ID from recent projects, stored in localStorage
	 * @todo Probably need to move somewhere else, doesn't really relate to this store
	 *
	 * @param {Object} context Context object
	 * @param {number} projectId Selected project ID
	 */
	removeFromRecentProjects(context, projectId) {
		let recentProjects = ls.get('recentProjects', { uid: null, items: [] });

		if (!Array.isArray(recentProjects.items) || recentProjects.items.length === 0) {
			return;
		}

		recentProjects.items = recentProjects.items.filter(id => id !== projectId);

		ls.set('recentProjects', recentProjects);
	}

};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions
};
